<template>
    <div>
        <ts-page-title
            :title="$t('cashTransferReport.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: $t('cashTransferReport.pageTitle'), active: true }
            ]"
        />

        <ts-panel>
            <!-- Filter bar -->
            <ts-panel-wrapper>
                <div class="tw-flex tw-flex-wrap tw-gap-3 tw-items-end">
                    <!-- Date From -->
                    <div>
                        <label class="tw-block tw-text-xs tw-text-gray-500 tw-mb-1">{{ $t('cashTransferReport.dateFrom') }}</label>
                        <DatePicker
                            type="date"
                            format="dd-MM-yyyy"
                            :value="model.date_from"
                            @on-change="model.date_from = $event"
                            style="width: 140px"
                            :placeholder="$t('cashTransferReport.dateFrom')"
                        />
                    </div>

                    <!-- Date To -->
                    <div>
                        <label class="tw-block tw-text-xs tw-text-gray-500 tw-mb-1">{{ $t('cashTransferReport.dateTo') }}</label>
                        <DatePicker
                            type="date"
                            format="dd-MM-yyyy"
                            :value="model.date_to"
                            @on-change="model.date_to = $event"
                            style="width: 140px"
                            :placeholder="$t('cashTransferReport.dateTo')"
                        />
                    </div>

                    <!-- Branch -->
                    <div>
                        <label class="tw-block tw-text-xs tw-text-gray-500 tw-mb-1">{{ $t('branchName') }}</label>
                        <ts-branch-filter
                            @filter="value => (model.branch_id = value)"
                            style="width: 160px"
                        />
                    </div>

                    <!-- Transfer By -->
                    <div>
                        <label class="tw-block tw-text-xs tw-text-gray-500 tw-mb-1">{{ $t('cashTransferReport.transferBy') }}</label>
                        <Select
                            v-model="model.employee_id"
                            clearable
                            filterable
                            :remote-method="searchEmployee"
                            style="width: 200px"
                            :placeholder="$t('all')"
                        >
                            <Option
                                v-for="emp in employees"
                                :key="emp.employee_id"
                                :value="emp.employee_id"
                            >
                                {{ emp.employee_name_en }}
                            </Option>
                        </Select>
                    </div>

                    <!-- Status -->
                    <div>
                        <label class="tw-block tw-text-xs tw-text-gray-500 tw-mb-1">{{ $t('cashTransferReport.status') }}</label>
                        <Select
                            v-model="model.approval_status"
                            multiple
                            clearable
                            style="width: 180px"
                            :placeholder="$t('all')"
                        >
                            <Option :value="0">{{ $t('cashTransfer.pending') }}</Option>
                            <Option :value="1">{{ $t('cashTransfer.partiallyApproved') }}</Option>
                            <Option :value="2">{{ $t('cashTransfer.approved') }}</Option>
                            <Option :value="3">{{ $t('cashTransfer.rejected') }}</Option>
                        </Select>
                    </div>

                    <!-- Currency -->
                    <div>
                        <label class="tw-block tw-text-xs tw-text-gray-500 tw-mb-1">{{ $t('cashTransferReport.currency') }}</label>
                        <Select
                            v-model="model.currency_id"
                            multiple
                            clearable
                            style="width: 130px"
                            :placeholder="$t('all')"
                        >
                            <Option
                                v-for="c in currencies"
                                :key="c.currency_id"
                                :value="c.currency_id"
                            >
                                {{ c.currency_code }}
                            </Option>
                        </Select>
                    </div>

                    <!-- Bank -->
                    <div>
                        <label class="tw-block tw-text-xs tw-text-gray-500 tw-mb-1">{{ $t('cashTransferReport.bank') }}</label>
                        <Select
                            v-model="model.bank_id"
                            clearable
                            style="width: 160px"
                            :placeholder="$t('all')"
                        >
                            <Option
                                v-for="b in banks"
                                :key="b.bank_id"
                                :value="b.bank_id"
                            >
                                {{ b.bank_name }}
                            </Option>
                        </Select>
                    </div>

                    <!-- Search -->
                    <Button type="primary" @click="fetchData()">
                        <i class="fas fa-search"></i>
                    </Button>
                </div>

                <!-- Export buttons -->
                <div class="tw-flex tw-gap-2 tw-mt-3">
                    <Button type="success" ghost :loading="exporting" @click="onExportExcel">
                        <i class="far fa-file-excel tw-mr-1"></i> Excel
                    </Button>
                    <Button type="error" ghost :loading="waiting" @click="onPreviewPdf">
                        <i class="far fa-file-pdf tw-mr-1"></i> PDF
                    </Button>
                </div>
            </ts-panel-wrapper>

            <!-- Table -->
            <Table
                :columns="columns"
                :data="resources"
                :loading="loading"
                size="small"
                max-height="650"
                stripe
            />

            <div class="tw-flex tw-items-center tw-justify-end tw-p-4">
                <ts-i-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                />
            </div>
        </ts-panel>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { debounce } from 'lodash'
import moment from 'moment'
import * as FileDownload from 'downloadjs'

export default {
    name: 'CashTransferReport',
    data () {
        return {
            loading: false,
            waiting: false,
            exporting: false,
            model: {
                date_from: moment().startOf('month').format('DD-MM-YYYY'),
                date_to: moment().format('DD-MM-YYYY'),
                branch_id: [],
                employee_id: null,
                approval_status: [],
                currency_id: [],
                bank_id: null
            }
        }
    },
    computed: {
        ...mapState('report/cashTransferReport', ['resources', 'pagination', 'employees', 'banks', 'currencies']),
        columns () {
            return [
                {
                    title: this.$t('cashTransferReport.no'),
                    width: 55,
                    align: 'center',
                    render: (h, params) => h('span', params.index + 1)
                },
                {
                    title: this.$t('cashTransferReport.transferNo'),
                    key: 'transfer_no',
                    minWidth: 130,
                    render: (h, { row }) =>
                        h('span', { class: 'tw-font-bold text-primary' }, row.transfer_no)
                },
                {
                    title: this.$t('cashTransferReport.branch'),
                    minWidth: 120,
                    render: (h, { row }) =>
                        h('span', row.branch ? row.branch.branch_name_en : '')
                },
                {
                    title: this.$t('cashTransferReport.transferBy'),
                    minWidth: 160,
                    render: (h, { row }) => {
                        if (!row.transfer_by) return h('span', '')
                        return h('div', [
                            h('div', { class: 'tw-font-semibold tw-text-gray-800 tw-text-sm' },
                                row.transfer_by.employee_name_en),
                            h('div', { class: 'tw-text-xs tw-text-gray-400' },
                                row.transfer_by.logicalPosition
                                    ? row.transfer_by.logicalPosition.position_name_en
                                    : '')
                        ])
                    }
                },
                {
                    title: this.$t('cashTransferReport.transferDate'),
                    minWidth: 110,
                    align: 'center',
                    key: 'transfer_date'
                },
                {
                    title: this.$t('cashTransferReport.amount'),
                    minWidth: 130,
                    align: 'right',
                    render: (h, { row }) =>
                        h('span', { class: 'tw-font-bold' },
                            this.$store.getters.formatCurrencyWithCode(
                                row.transfer_amount,
                                row.currency
                            ))
                },
                {
                    title: this.$t('cashTransferReport.remark'),
                    minWidth: 160,
                    render: (h, { row }) => {
                        if (!row.remark) return h('span', '')
                        return row.remark.length > 50
                            ? h('i-tooltip', {
                                props: { content: row.remark, transfer: true, maxWidth: 320, theme: 'light' }
                              }, [h('span', { style: 'cursor:default' }, row.remark.slice(0, 50) + '…')])
                            : h('span', row.remark)
                    }
                },
                {
                    title: this.$t('cashTransferReport.bankReceived'),
                    minWidth: 160,
                    render: (h, { row }) => {
                        if (!row.bank_name) return h('span', { class: 'tw-text-gray-300' }, '—')
                        return h('div', [
                            h('div', { class: 'tw-font-semibold tw-text-gray-800', style: 'font-size:12px' },
                                row.bank_name),
                            h('div', { class: 'tw-text-gray-500', style: 'font-size:11px' },
                                row.bank_account_no),
                            h('div', { class: 'tw-text-gray-400', style: 'font-size:11px' },
                                row.bank_account_name)
                        ])
                    }
                },
                {
                    title: this.$t('cashTransferReport.status'),
                    minWidth: 120,
                    align: 'center',
                    render: (h, { row }) =>
                        h('span', { class: this.statusStyle(row.approval_status) },
                            this.statusLabel(row.approval_status))
                },
                {
                    title: this.$t('cashTransferReport.bankSlipDate'),
                    minWidth: 110,
                    align: 'center',
                    key: 'bank_slip_date',
                    render: (h, { row }) =>
                        h('span', row.bank_slip_date || '—')
                },
                {
                    title: this.$t('cashTransferReport.approvedComment'),
                    minWidth: 160,
                    render: (h, { row }) => {
                        if (!row.approved_comment)
                            return h('span', { class: 'tw-text-gray-300' }, '—')
                        return row.approved_comment.length > 40
                            ? h('i-tooltip', {
                                props: { content: row.approved_comment, transfer: true, maxWidth: 260, theme: 'light' }
                              }, [h('span', {
                                style: 'font-size:11px; cursor:default; color:#6b7280; font-style:italic'
                              }, row.approved_comment.slice(0, 40) + '…')])
                            : h('span', { style: 'font-size:11px; color:#6b7280; font-style:italic' },
                                row.approved_comment)
                    }
                }
            ]
        }
    },
    created () {
        this.$store.dispatch('report/cashTransferReport/fetchBanks')
        this.$store.dispatch('report/cashTransferReport/fetchCurrencies')
        this.$store.dispatch('report/cashTransferReport/fetchEmployees')
    },
    methods: {
        fetchData (attributes = {}) {
            this.loading = true
            this.$store
                .dispatch('report/cashTransferReport/fetch', {
                    ...attributes,
                    ...this.filterParams()
                })
                .finally(() => (this.loading = false))
        },
        searchEmployee: debounce(function (query) {
            this.$store.dispatch('report/cashTransferReport/fetchEmployees', { search: query })
        }, 500),
        filterParams () {
            return {
                date_from: this.model.date_from,
                date_to: this.model.date_to,
                branch_id: this.model.branch_id,
                employee_id: this.model.employee_id,
                approval_status: this.model.approval_status,
                currency_id: this.model.currency_id,
                bank_id: this.model.bank_id
            }
        },
        onPreviewPdf () {
            this.waiting = true
            this.$store
                .dispatch('report/cashTransferReport/previewPdf', this.filterParams())
                .then(response => window.open(response.url, '_blank'))
                .catch(error => this.notice({ type: 'error', text: error.message }))
                .finally(() => (this.waiting = false))
        },
        onExportExcel () {
            this.exporting = true
            this.$store
                .dispatch('report/cashTransferReport/exportExcel', this.filterParams())
                .then(response => {
                    FileDownload(response.data, 'cash-transfer-report.xlsx')
                })
                .catch(error => this.notice({ type: 'error', text: error.message }))
                .finally(() => (this.exporting = false))
        },
        statusLabel (status) {
            return { 0: 'Pending', 1: 'Partially Approved', 2: 'Approved', 3: 'Rejected' }[status] || ''
        },
        statusStyle (status) {
            return {
                0: 'badge bg-warning rounded-pill',
                1: 'badge bg-info rounded-pill',
                2: 'badge bg-success rounded-pill',
                3: 'badge bg-danger rounded-pill'
            }[status] || 'badge bg-secondary rounded-pill'
        },
        notice (not) {
            this.$Notice[not.type]({
                title: this.$t('cashTransferReport.pageTitle'),
                desc: not.text
            })
        }
    }
}
</script>
